/**
 * Shared profile data hook -- business logic extracted from ProfileClient.
 * Used by both desktop ProfileClient and mobile MobileProfile.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { transformBackendPost, FeedPost } from './useFeedData';
import { getProfile } from '../../../lib/api-client';
import resolveAvatar from '../../../lib/resolveAvatar';
import { useAuth } from '../../../context/AuthContext';

import { fetchWithAuth } from '../../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProfileData = {
  id: string;
  username: string;
  displayName: string;
  bio: string;
  title: string;
  avatar?: string;
  isCurrentUser: boolean;
  followerCount: number;
  followingCount: number;
  postCount: number;
  friendCount: number;
  profileCustomization?: any;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProfileData(userId?: string) {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isMutualFollowers, setIsMutualFollowers] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // -------------------------------------------------------------------
  // Fetch profile + posts
  // -------------------------------------------------------------------

  const fetchProfile = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);

    try {
      const data = await getProfile(userId);
      if (!mountedRef.current) return;

      const user = data.user as any;
      // IDs are Company ULID strings (not numeric) — compare as strings.
      const currentUserId = authUser ? String(authUser.id) : null;
      const profileUserId = String(user.id);

      setProfile({
        id: String(user.id),
        username: user.username,
        displayName: user.displayName || user.profileName || user.username,
        bio: user.bio || user.profileBio || '',
        title: user.profileTitle || user.title || 'Earthling',
        avatar: resolveAvatar(user.avatar || user.profilePicture || user.image),
        isCurrentUser: currentUserId === profileUserId,
        followerCount: user.followerCount ?? user._count?.followers ?? 0,
        followingCount: user.followingCount ?? user._count?.following ?? 0,
        postCount: user.postCount ?? user._count?.posts ?? user.posts?.length ?? 0,
        friendCount: user.friendCount ?? 0,
        profileCustomization: user.profileCustomization || user.customization,
      });

      setIsFollowing(user.isFollowing || false);
      setIsMutualFollowers(user.isMutualFollowers || false);

      // Transform posts
      const transformedPosts: FeedPost[] = (user.posts || []).map((p: any) =>
        transformBackendPost({
          ...p,
          authorName: user.displayName || user.profileName || user.username,
          authorId: user.id,
          authorAvatar: user.avatar || user.profilePicture || user.image,
          authorUsername: user.username,
        })
      );
      setPosts(transformedPosts);
    } catch {
      /* silent */
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [userId, authUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // -------------------------------------------------------------------
  // Follow / Unfollow
  // -------------------------------------------------------------------

  const followUser = useCallback(async () => {
    if (!userId) return;
    try {
      const { followUser: apiFollow } = await import('../../../lib/api-client');
      await apiFollow(userId);
      if (mountedRef.current) {
        setIsFollowing(true);
        setProfile(prev => prev ? { ...prev, followerCount: prev.followerCount + 1 } : prev);
      }
      window.dispatchEvent(new CustomEvent('followingUpdated'));
    } catch {
      /* silent */
    }
  }, [userId]);

  const unfollowUser = useCallback(async () => {
    if (!userId) return;
    try {
      const { unfollowUser: apiUnfollow } = await import('../../../lib/api-client');
      await apiUnfollow(userId);
      if (mountedRef.current) {
        setIsFollowing(false);
        setProfile(prev => prev ? { ...prev, followerCount: Math.max(0, prev.followerCount - 1) } : prev);
      }
      window.dispatchEvent(new CustomEvent('followingUpdated'));
    } catch {
      /* silent */
    }
  }, [userId]);

  // -------------------------------------------------------------------
  // Like / Dislike (optimistic updates)
  // -------------------------------------------------------------------

  const likePost = useCallback(async (postId: string) => {
    const target = posts.find(p => p.id === postId);
    if (!target) return;

    const wasLiked = target.liked;
    const initialLikes = target.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, liked: newLiked, likes: newLikes } : p
    ));

    try {
      const res = await fetchWithAuth(`/api/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        // Revert
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, liked: wasLiked, likes: initialLikes } : p
        ));
      }
    } catch {
      // Revert on error
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, liked: wasLiked, likes: initialLikes } : p
      ));
    }
  }, [posts]);

  const dislikePost = useCallback(async (postId: string) => {
    const target = posts.find(p => p.id === postId);
    if (!target) return;

    const wasDisliked = target.disliked;
    const initialDislikes = target.dislikes;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? (initialDislikes || 0) - 1 : (initialDislikes || 0) + 1;

    // Optimistic update
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, disliked: newDisliked, dislikes: newDislikes } : p
    ));

    try {
      const res = await fetchWithAuth(`/api/posts/${postId}/dislike`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        // Revert
        setPosts(prev => prev.map(p =>
          p.id === postId ? { ...p, disliked: wasDisliked, dislikes: initialDislikes } : p
        ));
      }
    } catch {
      // Revert on error
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, disliked: wasDisliked, dislikes: initialDislikes } : p
      ));
    }
  }, [posts]);

  // -------------------------------------------------------------------
  // Refresh
  // -------------------------------------------------------------------

  const refreshProfile = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    posts,
    isLoading,
    isFollowing,
    isMutualFollowers,
    followUser,
    unfollowUser,
    likePost,
    dislikePost,
    refreshProfile,
  };
}
